<template>
  <div class="modal-overlay">
    <div
      ref="dialogRef"
      class="confirmation-modal modal-content"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inactivity-warning-title"
      aria-describedby="inactivity-warning-body"
      tabindex="-1"
    >
      <div class="modal-header">
        <h4 id="inactivity-warning-title" class="text-text font-semibold text-xl">{{ title }}</h4>
      </div>
      <div id="inactivity-warning-body" class="modal-body">
        <div class="text-text">
          You've been inactive for a while. For your security, you'll be signed out in
          <span class="font-semibold">{{ formattedCountdown }}</span
          >.
        </div>
        <div class="flex justify-end mt-4">
          <Button class="button-primary relative justify-center !px-4" @click.prevent="$emit('stay')">
            Stay logged in
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import Button from '@/components/Button.vue';

const props = withDefaults(defineProps<{ secondsLeft: number; title?: string }>(), {
  title: 'Are you still there?',
});

defineEmits<{ (e: 'stay'): void }>();

// Local 1s ticker for a smooth countdown, re-synced from the 30s poll's
// authoritative `secondsLeft` whenever it changes.
const displaySeconds = ref<number>(props.secondsLeft);
const dialogRef = ref<HTMLElement | null>(null);
let tickId: ReturnType<typeof setInterval> | undefined;

watch(
  () => props.secondsLeft,
  (value) => {
    displaySeconds.value = value;
  },
);

const formattedCountdown = computed<string>(() => {
  const total = Math.max(0, displaySeconds.value);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

onMounted(() => {
  // Move focus into the forced dialog so keyboard and screen-reader users land
  // on the warning rather than the now-inert background.
  dialogRef.value?.focus();
  tickId = setInterval(() => {
    if (displaySeconds.value > 0) {
      displaySeconds.value -= 1;
    }
  }, 1000);
});

onUnmounted(() => {
  if (tickId) {
    clearInterval(tickId);
  }
});
</script>
